import {
  normaliserTelephone,
  telephoneLisible,
  whatsappLisible,
  type CandidatureDetail,
  type DocumentResume,
} from '@rh/shared';
import { dateHeure } from '../format';
import { telechargerDocument } from '../telechargement';

/**
 * Ce que le candidat a écrit dans le formulaire public (lot 6), sur sa fiche.
 *
 * Le CV arrivait déjà dans « CV et documents » — le worker le rattache à la
 * candidature — mais la SAISIE ne se lisait nulle part : elle dormait dans
 * `soumissions_formulaire.donnees`. Or c'est la seule source où le candidat
 * parle de lui-même avec ses mots ; la fiche, elle, ne garde que ce que
 * l'application en a déduit.
 *
 * Les valeurs sont rendues **telles qu'elles ont été tapées**, sans reformatage
 * ni correction : c'est tout l'intérêt du bloc. Quand la fiche a été corrigée
 * depuis, l'écart est signalé plutôt que tu.
 */
export function FormulaireRecu({
  fiche,
  onOuvrirCv,
}: {
  fiche: CandidatureDetail;
  onOuvrirCv: (document: DocumentResume) => void;
}): JSX.Element | null {
  const formulaire = fiche.formulaire;
  if (!formulaire) return null;
  const { saisie, cv } = formulaire;

  /**
   * L'écart se mesure sur la valeur NORMALISÉE, jamais sur le texte brut :
   * « 98 123 456 » et « +21698123456 » sont le même numéro, et les signaler
   * comme différents apprendrait aux utilisateurs à ignorer la mention.
   */
  const telephoneDiverge =
    saisie?.telephone != null &&
    fiche.telephone != null &&
    normaliserTelephone(saisie.telephone) !== fiche.telephone;
  const emailDiverge =
    saisie?.email != null &&
    fiche.email != null &&
    saisie.email.toLowerCase() !== fiche.email.toLowerCase();

  return (
    <section className="bloc formulaire-recu">
      <div className="formulaire-entete">
        <h3>Formulaire public</h3>
        <span className="badge-langue" title={LIBELLE_LANGUE[formulaire.langue]}>
          {formulaire.langue.toUpperCase()}
        </span>
        <p className="mention formulaire-recu-le">Reçu le {dateHeure(formulaire.recuLe)}</p>
      </div>

      {saisie === null ? (
        <p className="mention formulaire-efface">
          Saisie effacée à l’anonymisation. La date de réception et la langue restent :
          elles ne désignent personne.
        </p>
      ) : (
        <dl className="formulaire-reponses">
          <dt>Nom saisi</dt>
          <dd>{saisie.nom || <span className="mention">non renseigné</span>}</dd>

          {saisie.coordonneesMasquees ? (
            <>
              <dt>Coordonnées</dt>
              <dd className="mention">masquées pour votre rôle</dd>
            </>
          ) : (
            <>
              <dt>Téléphone</dt>
              <dd>
                {saisie.telephone ? (
                  <>
                    {saisie.telephone}
                    {telephoneDiverge && (
                      <span className="formulaire-ecart">
                        la fiche porte {telephoneLisible(fiche.telephone!)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="mention">non renseigné</span>
                )}
              </dd>

              <dt>WhatsApp</dt>
              <dd>
                {saisie.whatsappEstLeTelephone ? (
                  <span className="mention">le même numéro que le téléphone</span>
                ) : saisie.whatsapp ? (
                  whatsappLisible(saisie.whatsapp)
                ) : (
                  <span className="mention">non renseigné</span>
                )}
              </dd>

              <dt>E-mail</dt>
              <dd>
                {saisie.email ? (
                  <>
                    {saisie.email}
                    {emailDiverge && (
                      <span className="formulaire-ecart">la fiche porte {fiche.email}</span>
                    )}
                  </>
                ) : (
                  <span className="mention">non renseigné</span>
                )}
              </dd>
            </>
          )}

          <dt>Ville</dt>
          <dd>{saisie.ville || <span className="mention">non renseignée</span>}</dd>

          <dt>Expérience</dt>
          <dd>
            {saisie.experience ? (
              /* L'arabe garde son sens de lecture : un texte écrit en arabe
                 rendu de gauche à droite est illisible pour qui le relit. */
              <p
                className="formulaire-experience"
                lang={formulaire.langue}
                dir={formulaire.langue === 'ar' ? 'rtl' : 'ltr'}
              >
                {saisie.experience}
              </p>
            ) : (
              <span className="mention">non renseignée</span>
            )}
          </dd>
        </dl>
      )}

      {cv ? (
        <div className="formulaire-cv">
          <span className="formulaire-cv-corps">
            <button type="button" className="lien-doc" onClick={() => onOuvrirCv(cv)}>
              {cv.nomFichier}
            </button>
            <span className="mention">
              CV déposé avec le formulaire
              {formulaire.cvDUnEnvoiPrecedent && ' — lors d’un envoi précédent'}
              {!cv.lisible && ' · texte non extrait'}
            </span>
          </span>
          <button
            type="button"
            className="bouton-second"
            onClick={() => void telechargerDocument(cv.id, cv.nomFichier)}
          >
            Télécharger
          </button>
        </div>
      ) : (
        saisie !== null && (
          <p className="mention formulaire-sans-cv">
            Aucun CV joint : le formulaire a été envoyé sans fichier.
          </p>
        )
      )}

      <p className="mention formulaire-pied">
        Information acceptée : mention {formulaire.mentionVersion}
      </p>
    </section>
  );
}

const LIBELLE_LANGUE = { fr: 'Formulaire rempli en français', ar: 'Formulaire rempli en arabe' };
